/**
 * 市场部SDR跟进记录
 * @author wzwwu
 */
import React, { useCallback, useMemo } from 'react'
import { CommonFilters, PageHeader, ReplayRecord, ShowSMS, TableDownload, UserAvatar, TreeDataSimple, CommonCascador } from '@components'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { Card, message, Table, Tooltip, Select, Popover } from 'antd'
import { exportHistorySdrFollowList, getSDRFollowRecordList } from '@services/customer'
import { ExtendItem, HistorySdrFollowListFilter, needTotalEnum, HistorySdrFollowListItem } from '@types'
import { TableColumnProps, useTableColumn } from '@hooks'
import { Link } from 'react-router-dom'
import { formatTime, parseExtend, parseFollowExtend, trimStr, isEmptyArray } from '@library'
import { getSortOrder, getTableXwidth, TableIdEnum } from '@configs/table'
import { ConfigTableHeader } from '../../../components/ConfigTableHeader'
import { cosGetObjectUrl } from '../../../library/cosUtils'
import downloadByATag from '../../../library/downloadByATag'
import moment from 'moment'
import { useTableService } from '@hooks/useTableService'
import { antdSortMap } from '@configs/map'
import { TableProps } from 'antd/lib/table'
import { clone } from 'lodash'
import { FieldItem } from 'src/components/CommonFilters/configs'
import { industryProducts } from 'src/components/CommonCascador/configs'
import { CommunicateResultOptions } from '@pages/customer/detail/pages/detail-tabs/AddFollowRecordPanelSdr'
import AiCustomerPersonas from '@pages/customer/detail/pages/detail-tabs/AiCustomerPersonas'
import { globalStore, sessionStore } from '@store'
import { moduleId } from '@configs'

type HistoryFollowData = HistorySdrFollowListItem & {
  extendObj?: {[key: string]: any},
  extendArr?: ExtendItem[],
  intention?: string
  followType?: string
  intentionSummary?: {[key: string]: any},
  leadEntityIds?: string // 线索实体ID列表(逗号分割)
}

const initFilterValue = { lastFollowTime: [moment().subtract(1, 'month'), moment()] }
const initFilter: HistorySdrFollowListFilter = {
  page: 1,
  pageSize: 10,
  needTotal: needTotalEnum.ALL,
  // sort: 'createTime',
  lastFollowBeginTime: formatTime(initFilterValue.lastFollowTime[0], 'start'),
  lastFollowEndTime: formatTime(initFilterValue.lastFollowTime[1], 'end'),
  // sortType: sortTypeEnum.DESC,
}
// 这里必须把用户选择的客户意向区分成一级和二级意向
const formatIntention = (intention: { val: number[], list: TreeDataSimple[] }) => {
  const { val, list } = intention || {}
  const intentionId = val?.filter?.(number => list?.find(i => i.value === number))
  const subIntentionId = val?.filter?.(number => !intentionId?.find(i => i === number))
  return {
    intentionId: isEmptyArray(intentionId) ? undefined : intentionId,
    subIntentionId: isEmptyArray(subIntentionId) ? undefined : subIntentionId,
  }
}

// 跟进记录筛选框
export const sdrFollowRecordFilterItems: FieldItem[] = [
  { fieldName: 'cid', label: 'cid', type: 'multiTags' },
  { fieldName: 'uin', label: 'uin', type: 'multiTags' },
  { fieldName: 'name', label: '客户名称', type: 'customerNameSelect' },
  { fieldName: 'followType', label: '沟通方式', type: 'followType' },
  { fieldName: 'ruleType', label: '任务类型', type: 'ruleType' },
  { fieldName: 'intentionId', label: '客户意向', type: 'intention', handleValue: formatIntention },
  {
    fieldName: 'interestProduct',
    label: '感兴趣的产品',
    render: () => <CommonCascador {...industryProducts} changeOnSelect />,
    handleValue: value => {
      const [interestSmallProduct, interestSubProduct] = value || [undefined, undefined]
      return { interestSmallProduct, interestSubProduct }
    },
  },
  { fieldName: 'creator', label: '历史跟进人', type: 'staffSelectMulti' },
  {
    fieldName: 'lastFollowTime',
    label: '跟进时间',
    type: 'rangePicker',
    handleValue: val => ({
      lastFollowBeginTime: formatTime(val?.[0], 'start'),
      lastFollowEndTime: formatTime(val?.[1], 'end'),
    }),
  },
  { fieldName: 'taskId', label: '任务ID', type: 'inputNumber' },
  { fieldName: 'leadId', label: '线索ID', type: 'inputNumber' },
  {
    fieldName: 'sourceTagIds',
    label: '客户来源',
    type: 'sourceTag',
    handleValue: val => ({ sourcePrimaryTagId: val?.[0], sourceSecondaryTagId: val?.[1] }),
  },
  { fieldName: 'groupId', label: '客户所属组', type: 'groupId' },
  {
    fieldName: 'leadTime',
    label: '线索创建时间',
    type: 'rangePicker',
    handleValue: val => ({
      leadBeginTime: formatTime(val?.[0], 'start'),
      leadEndTime: formatTime(val?.[1], 'end'),
    }),
  },
  { fieldName: 'publicTagId', label: '公共标签', type: 'publicTag' },
  { fieldName: 'privateTagId', label: '个人标签', type: 'privateTag' },
  { fieldName: 'phone', label: '电话', type: 'input' },
  { fieldName: 'qq', label: 'QQ', type: 'input' },
  { fieldName: 'communicateResult', label: '是否有效沟通', render: () => <Select allowClear options={CommunicateResultOptions} /> },
  { fieldName: 'batchId', label: '批次id', type: 'batchId' },
  // { fieldName: 'leadReleaseSourceId', label: '来源方线索ID', type: 'input' },
]

export default function FollowRecordSdrPage () {
  const {
    loading,
    list,
    pagination,
    filter,
    setFilter,
    resetFilter,
    rowKey,
  } = useTableService(getSDRFollowRecordList, initFilter, '获取跟进记录失败')

  const userInfo = sessionStore(s => s.userInfo)
  const authPointMap = globalStore(s => s.authPointMap)

  const historySdrFollowList: HistoryFollowData[] = useMemo(() => list.map(item => {
    const historyFollowData: any = clone(item)
    const extendObj = parseFollowExtend(item?.extend)
    historyFollowData.extendObj = extendObj
    historyFollowData.extendArr = parseExtend(item?.extend)
    historyFollowData.intention = `${item.intentionName}-${item.subIntentionName}`
    historyFollowData.followType = item.followType.join?.(',')
    historyFollowData.interestProduct = `${item.interestSmallProductName}-${item.interestSubProductName}`
    historyFollowData.touchIssue = `${item?.touchIssueCh}-${item?.touchIssueDetail}`
    // 从leads数组中提取线索实体ID并用逗号分割
    historyFollowData.leadEntityIds = item.leads
      ?.map(lead => lead.leadExtend?.find?.(extend => extend.key === 'id' && extend.keyName === '线索实体ID')?.keyValue)
      ?.filter(Boolean)
      ?.join(', ') || '-'
    return historyFollowData
  }), [list])

  const onTableChange: TableProps<HistorySdrFollowListItem>['onChange'] = (pagination, filters, sorter) => {
    const sort = getSortOrder<HistorySdrFollowListFilter>(sorter)
    setFilter(sort)
  }

  const downloadFile = useCallback(async (key: string) => {
    const [url, err] = await cosGetObjectUrl(key)
    if (err) {
      message.error(`下载失败: ${err.message}`)
    } else {
      downloadByATag(key, url)
    }
  }, [])

  const renderTitle = (record) => {
    return <AiCustomerPersonas id={record?.leadIds} title="💡AI意向提炼" type="intention" overClass="table-popup" expandable={false} originData={record.intentionSummary} />
  }

  const columns: TableColumnProps<HistoryFollowData>[] = useMemo(() => [
    {
      title: '跟进时间',
      dataIndex: 'createTime',
      width: 180, // 注意和时间相关都建议180
      fixed: true,
      sorter: true,
      sortOrder: filter.sort === 'createTime' ? antdSortMap[filter.sortType] : undefined,
    },
    { title: 'cid', dataIndex: 'cid', width: 120 },
    {
      title: '客户名称',
      dataIndex: 'customerName',
      width: 180,
      render: (name, record) => (
        <Link target="_blank" to={`/customer/${record.customerId}?from=${encodeURIComponent(window.location.pathname)}`}>
          {trimStr(name, '-')}
        </Link>),
    },
    { title: '联系人', dataIndex: 'touchCustomerName', width: 120 },
    { title: '职务', dataIndex: 'touchCustomerTitle', width: 120 },
    { title: '沟通方式', dataIndex: 'followType', width: 120 },
    { title: '客户意向', dataIndex: 'intention', width: 200 },
    { title: '是否有效沟通', dataIndex: 'communicateResult', width: 120 },
    {
      title: '本次跟进内容',
      dataIndex: 'remark',
      ellipsis: true,
      width: 120,
      render: remark => <Tooltip placement="topLeft" title={remark}>{remark}</Tooltip>,
    },
    { title: '感兴趣的产品', dataIndex: 'interestProduct', width: 120 },
    { title: '历史跟进人', dataIndex: 'creator', width: 220, render: value => <UserAvatar salesName={value} /> },
    { title: '任务ID', dataIndex: 'taskIds', width: 120 },
    { title: '针对线索ID', dataIndex: 'leadIds', width: 120 },
    { title: '线索实体ID', dataIndex: 'leadEntityIds', width: 120 },
    // { title: '来源方线索ID', dataIndex: 'leadReleaseSourceId', width: 120 },
    {
      title: (<>下次跟进时间<Tooltip title="代表当时跟进填写的下次跟进时间，最近一次跟进记录的下次跟进时间会更新到对应任务的下次跟进时间"><QuestionCircleOutlined /></Tooltip></>),
      _title: '下次跟进时间',
      dataIndex: 'nextFollowTime',
      width: 180,
    },
    {
      title: '附件',
      dataIndex: 'fileUpload',
      width: 200,
      render: (fileUpload, record) => {
        const keys = record.extendObj?.fileUpload as unknown as string[]
        if (Array.isArray(keys)) {
          return keys?.map((key, index) => <a key={key} onClick={() => downloadFile(key)}>附件{index + 1};</a>)
        } else {
          return '-'
        }
      },
    },
    { title: '跟进问题', dataIndex: 'touchIssue', width: 120 },
    {
      title: '操作',
      dataIndex: 'actions',
      width: 180,
      render: (actions, record) => {
        return [].concat(
          (record?.extendObj?.phoneLogIds?.split(',') ?? []).map(id => <ReplayRecord className="mr5" logId={id} title={`与${record?.customerName}的通话录音`} key={id} />),
          (record?.extendObj?.msgLogIds?.split(',') ?? []).map(id => <ShowSMS logId={id} className="mr5" key={id} />),
          // 临时隐藏意向提炼组件 - 通过添加false条件来禁用
          false && authPointMap.tccPermissionAiLeadTransferExt1?.result && ([moduleId.SDR_CLEAN].includes(userInfo.belongModule)) && record?.intentionSummary && <Popover placement="topLeft" content={renderTitle(record)}><a>意向提炼</a></Popover>,
        )
      },
    },
  ], [filter.sort, filter.sortType])
  const [columnConfigs, columnProps, forceRequest] = useTableColumn(TableIdEnum.FOLLOW_RECORD_SDR, columns, ['customerName', 'actions'])
  const tableXWidth = useMemo(() => getTableXwidth(columnProps), [columnProps])

  return (
    <>
      <PageHeader title="跟进记录" />
      <Card className="mt20 mb20">
        <CommonFilters
          className="custom-Label"
          initValue={initFilterValue}
          itemList={sdrFollowRecordFilterItems}
          onSubmit={filter => setFilter({ ...filter, page: 1, followType: filter?.followType && [filter?.followType] })}
          onReset={() => resetFilter(initFilter)}
        />
      </Card>
      <Card>
        <ConfigTableHeader columnConfigs={columnConfigs} tableId={TableIdEnum.FOLLOW_RECORD_SDR} onSuccess={() => forceRequest()} />
        <TableDownload className="ml20" exportMethod={exportHistorySdrFollowList} filter={filter} />
        <Table
          style={{ maxWidth: tableXWidth + 'px' }}
          tableLayout="fixed"
          className="mt20"
          loading={loading}
          pagination={pagination}
          scroll={{ x: tableXWidth }}
          rowKey={rowKey}
          dataSource={historySdrFollowList}
          columns={columnProps}
          onChange={onTableChange}
        />
      </Card>
    </>
  )
}
